import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Center } from "@astryxdesign/core/Center";
import { Stack, HStack, StackItem } from "@astryxdesign/core/Stack";
import { Card } from "@astryxdesign/core/Card";
import { Text, Heading } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { useAuth } from "../../contexts/AuthContext";
import AstryxTheme from "../../theme/AstryxTheme";
import logoAssescont from "../../assets/logo-assescont.png";

export default function Login() {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Clear errors when user starts typing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleFieldChange = (name) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };

  // Chamado pelo onClick do botão e pelo onEnter dos campos (o Button do
  // Astryx renderiza type="button", então o submit nativo do form não dispara).
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");

    // Basic validation
    if (!formData.email || !formData.password) {
      setError("Por favor, preencha todos os campos");
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Por favor, digite um email válido");
      setLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // Login successful - AuthContext will handle navigation
      } else {
        setError(result.message || "Credenciais inválidas");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Erro ao conectar com o servidor. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AstryxTheme>
      <Center>
        <Stack
          minHeight="100vh"
          width="100%"
          maxWidth={440}
          justify="center"
          gap={4}
          paddingInline={4}
          paddingBlock={6}
        >
          <Card padding={6}>
            <Stack gap={4}>
              <HStack gap={3} vAlign="center">
                <img
                  src={logoAssescont}
                  alt="Assescont"
                  width={72}
                  height={72}
                />
                <Stack gap={1}>
                  <Heading level={1}>NFSe Downloader</Heading>
                  <Text type="supporting">
                    Faça login para acessar o sistema.
                  </Text>
                </Stack>
              </HStack>

              <form onSubmit={handleSubmit}>
                <Stack gap={3}>
                  <TextInput
                    label="E-mail"
                    type="email"
                    htmlName="email"
                    placeholder="Digite seu email"
                    value={formData.email}
                    onChange={handleFieldChange("email")}
                    onEnter={handleSubmit}
                    isDisabled={loading}
                    hasAutoFocus
                  />
                  <HStack gap={2} vAlign="end">
                    <StackItem size="fill">
                      <TextInput
                        label="Senha"
                        type={showPassword ? "text" : "password"}
                        htmlName="password"
                        placeholder="Digite sua senha"
                        value={formData.password}
                        onChange={handleFieldChange("password")}
                        onEnter={handleSubmit}
                        isDisabled={loading}
                      />
                    </StackItem>
                    <IconButton
                      label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      icon={
                        <FontAwesomeIcon
                          icon={showPassword ? "eye-slash" : "eye"}
                        />
                      }
                      variant="ghost"
                      onClick={() => setShowPassword((v) => !v)}
                    />
                  </HStack>
                  <CheckboxInput
                    label="Lembrar de mim"
                    value={rememberMe}
                    onChange={setRememberMe}
                    isDisabled={loading}
                  />
                  {error && <Banner status="error" title={error} />}
                  <Button
                    label={loading ? "Entrando…" : "Entrar"}
                    variant="primary"
                    icon={<FontAwesomeIcon icon="sign-in-alt" />}
                    isLoading={loading}
                    onClick={handleSubmit}
                  />
                </Stack>
              </form>
            </Stack>
          </Card>

          <Stack gap={0.5}>
            <Text type="supporting" justify="center">
              NFSe Downloader v1.0.0
            </Text>
            <Text type="supporting" justify="center">
              Sistema de automação para download de NFSe
            </Text>
          </Stack>
        </Stack>
      </Center>
    </AstryxTheme>
  );
}
